import React, { useEffect, useState } from 'react';
import { ChevronDown, ExternalLink, Loader2 } from 'lucide-react';
import BottleScene from '../../components/DigitalTwin/BottleScene';
import SectionLabel from '../../components/ui-shree/SectionLabel';
import { fetchProvenanceStage } from '../../lib/api';

/**
 * LivingVessel — a quiet 3D scrollytelling moment for the Story page.
 *
 * Re-uses the refactored R3F `BottleScene` and binds its `targetLevel` prop
 * to a 4-stage narrative arc that mirrors the four chapters above:
 *
 *   I. Harvest  → empty vessel (the field, before the bottle exists)
 *   II. Distill → 35 % (steam captured, essence collected)
 *   III. Record → 70 % (chain hash signed, batch named)
 *   IV. Return  → 100 % (the ritual, the bottle in your hand)
 *
 * The user clicks a milestone; the liquid animates smoothly via the
 * existing `useFrame` lerp inside `Bottle`. No state escapes this section.
 */

const STAGES = [
  {
    id: 'harvest',
    no: 'I',
    label: 'The Harvest',
    level: 0.04,            // a breath above empty — the seed
    blurb:
      'A morning in Uttarakhand. The kewra is cut at 30°N before the sun reaches the soil. Nothing yet enters the vessel.',
    chip: 'Soil · 0 ml',
    evidence: {
      rows: [
        { l: 'Origin', v: 'Uttarakhand · 30.32°N, 78.05°E' },
        { l: 'Harvested', v: '2025-09-14T06:12Z' },
        { l: 'Hand', v: 'Kunwar Singh · co-op #7' },
        { l: 'Mode', v: 'Hand-cut, pre-sunrise' },
      ],
      ref: 'soil-harvest-0914',
    },
  },
  {
    id: 'distilled',
    no: 'II',
    label: 'The Distillation',
    level: 0.36,
    blurb:
      'Three brass funnels in a stone room in Heavener, Oklahoma. Steam is captured, weighed by hand, signed in linen.',
    chip: 'Atelier · 90 ml',
    evidence: {
      rows: [
        { l: 'Atelier', v: 'Heavener · Oklahoma · USA' },
        { l: 'Lot', v: 'KWR-042' },
        { l: 'Yield', v: '90 ml of 251 ml target' },
        { l: 'Pressed', v: '2025-09-21T11:48Z' },
      ],
      ref: 'lot-KWR-042',
    },
  },
  {
    id: 'recorded',
    no: 'III',
    label: 'The Recording',
    level: 0.72,
    blurb:
      'The batch is named. Its hash is written into a ledger that cannot be torn out. Memory, made incorruptible.',
    chip: 'On-chain · 181 ml',
    evidence: {
      rows: [
        { l: 'Chain hash', v: '0x7c9c…b14a' },
        { l: 'Block', v: '#18,442,701' },
        { l: 'Signed by', v: 'seva@shreesavaa.com' },
        { l: 'Recorded', v: '2025-09-21T17:02Z' },
      ],
      ref: '0x7c9cb14a',
    },
  },
  {
    id: 'returned',
    no: 'IV',
    label: 'The Pump · The Ritual',
    level: 1.0,
    blurb:
      'The bottle reaches a hand. The pump becomes a mechanical witness to your daily intention — every press is the Shadow of Seva, signed by the body.',
    chip: 'Pump witnessed · 251 ml',
    evidence: {
      rows: [
        { l: 'Vessel',         v: 'SH-042-0188' },
        { l: 'Interface',      v: 'Surya-gold ferrule · Chandra-silver lock ring' },
        { l: 'NTAG',           v: '22x · tamper-evident' },
        { l: 'Shadow of Seva', v: 'Press → IoT → Timechain' },
        { l: 'First dispense', v: '— awaits the guest —' },
        { l: 'Refill cycle',   v: 'Cold-fill ready · cellar' },
      ],
      ref: 'twin-SH-042-0188',
    },
  },
];

const AUTO_ADVANCE_MS = 4200;

const LivingVessel = () => {
  const [activeIdx, setActiveIdx] = useState(0);
  const [autoplay, setAutoplay] = useState(true);
  const [evidenceOpen, setEvidenceOpen] = useState(false);

  // Live evidence per stage. Keyed by stage.id. `null` = not yet fetched,
  // `false` = fetch failed (we fall back to the mock rows defined above).
  const [liveEvidence, setLiveEvidence] = useState({});

  // Gentle autoplay through the four stages until the guest takes control
  useEffect(() => {
    if (!autoplay) return;
    const t = setTimeout(() => {
      setActiveIdx((i) => (i + 1) % STAGES.length);
    }, AUTO_ADVANCE_MS);
    return () => clearTimeout(t);
  }, [activeIdx, autoplay]);

  // Close any open evidence drawer when the chapter changes — keeps the
  // proof contextual to the stage the guest is reading.
  useEffect(() => {
    setEvidenceOpen(false);
  }, [activeIdx]);

  const active = STAGES[activeIdx];

  // Lazy-fetch live evidence the first time a stage's drawer opens.
  useEffect(() => {
    if (!evidenceOpen) return;
    if (liveEvidence[active.id] !== undefined) return;
    let cancelled = false;
    fetchProvenanceStage(active.id)
      .then((data) => {
        if (!cancelled) {
          setLiveEvidence((prev) => ({ ...prev, [active.id]: data }));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLiveEvidence((prev) => ({ ...prev, [active.id]: false }));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [evidenceOpen, active.id, liveEvidence]);

  // Resolve the evidence we'll actually render. Live data wins; the static
  // bundle is the silent fallback so the section never goes blank.
  const live = liveEvidence[active.id];
  const isLive = live && live !== false && Array.isArray(live.rows);
  const isLoading = evidenceOpen && live === undefined;
  const evidenceRows = isLive ? live.rows : active.evidence?.rows || [];
  const evidenceRef = isLive ? live.ref : active.evidence?.ref;
  const chain = isLive ? live.chain : null;
  const chainStatus = isLive ? live.chain_status : null;

  return (
    <section
      data-testid="story-living-vessel"
      className="relative px-6 md:px-12 py-28 md:py-40 bg-[#F4F8F9] overflow-hidden"
    >
      {/* Soft ambient circle behind the bottle — depth, not decoration */}
      <div
        aria-hidden
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[720px] h-[720px] rounded-full blur-3xl opacity-50"
        style={{
          background:
            'radial-gradient(closest-side, #C1D7E2 0%, rgba(193,215,226,0) 70%)',
        }}
      />

      <div className="relative max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
        {/* Narrative column */}
        <div className="md:col-span-5 order-2 md:order-1">
          <SectionLabel testId="living-vessel-label">
            The Living Vessel
          </SectionLabel>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#7C9CB1] font-light leading-[1.05] mb-8">
            Watch the vessel
            <br />
            <em className="text-[#90B0BF]">fill itself</em>.
          </h2>

          {/* Chapter chip + blurb (animated by key change) */}
          <div
            key={active.id}
            data-testid={`living-vessel-stage-${active.id}`}
            className="story-fade-in min-h-[180px]"
          >
            <button
              type="button"
              onClick={() => {
                setAutoplay(false);
                setEvidenceOpen((v) => !v);
              }}
              data-testid={`living-vessel-evidence-toggle-${active.id}`}
              aria-expanded={evidenceOpen}
              className="group inline-flex items-center gap-2 mb-3 focus:outline-none"
            >
              <span className="font-mono text-[10px] tracking-[0.32em] uppercase text-[#90B0BF] group-hover:text-[#7C9CB1] transition-colors">
                Chapter {active.no} · {active.chip}
              </span>
              <ChevronDown
                size={12}
                strokeWidth={1.5}
                className={`text-[#90B0BF] group-hover:text-[#7C9CB1] transition-transform duration-300 ${
                  evidenceOpen ? 'rotate-180' : ''
                }`}
              />
            </button>
            <h3 className="font-serif text-2xl md:text-3xl text-[#7C9CB1] font-light leading-tight mb-5">
              {active.label}
            </h3>
            <p className="font-serif italic text-lg md:text-xl text-[#7C9CB1]/90 leading-relaxed max-w-md">
              {active.blurb}
            </p>

            {/* Evidence drawer — provenance rows, opens inline */}
            {evidenceOpen && (
              <div
                data-testid={`living-vessel-evidence-${active.id}`}
                className="evidence-fade-in mt-6 max-w-md border border-[#C1D7E2] bg-[#F4F8F9]/80 backdrop-blur-md p-5"
              >
                <div className="flex items-center justify-between mb-3">
                  <p className="font-mono text-[9px] tracking-[0.32em] uppercase text-[#90B0BF]">
                    Provenance · stage {active.no}
                  </p>
                  {isLoading && (
                    <Loader2
                      size={12}
                      strokeWidth={1.6}
                      className="animate-spin text-[#90B0BF]"
                      data-testid="living-vessel-evidence-loading"
                    />
                  )}
                  {isLive && (
                    <span
                      data-testid={`living-vessel-source-${active.id}`}
                      className="font-mono text-[9px] tracking-[0.28em] uppercase text-[#7C9CB1]"
                    >
                      Live · {live.source}
                    </span>
                  )}
                </div>
                <ul className="space-y-2 font-mono text-[11px] text-[#7C9CB1]">
                  {evidenceRows.map((row, i) => (
                    <li
                      key={`${row.l}-${i}`}
                      className="flex items-baseline justify-between gap-6 border-b border-[#C1D7E2]/60 pb-2 last:border-b-0 last:pb-0"
                    >
                      <span className="tracking-[0.22em] uppercase text-[#90B0BF]">
                        {row.l}
                      </span>
                      <span className="text-right">{row.v}</span>
                    </li>
                  ))}
                </ul>

                {/* Stage-III: real on-chain link if anchored, else honest status */}
                {active.id === 'recorded' && chain && chain.tx_hash && (
                  <a
                    href={chain.explorer_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="living-vessel-chain-link"
                    className="mt-4 group inline-flex items-center gap-2 px-3 py-2 border border-[#7C9CB1] text-[#7C9CB1] hover:bg-[#7C9CB1] hover:text-[#F4F8F9] transition-colors duration-300"
                  >
                    <span className="font-mono text-[10px] tracking-[0.24em] uppercase">
                      Open on {chain.network} · block #{chain.block_number}
                    </span>
                    <ExternalLink
                      size={11}
                      strokeWidth={1.5}
                      className="transition-transform group-hover:translate-x-0.5"
                    />
                  </a>
                )}
                {active.id === 'recorded' && !chain && chainStatus && (
                  <p
                    data-testid="living-vessel-chain-status"
                    className="mt-4 font-mono text-[9px] tracking-[0.28em] uppercase text-[#90B0BF]/85"
                  >
                    On-chain anchor · {chainStatus.replace(/_/g, ' ')}
                  </p>
                )}

                <p className="mt-4 font-mono text-[9px] tracking-[0.28em] uppercase text-[#90B0BF]/80">
                  {isLive ? (
                    <>Ref · {evidenceRef}</>
                  ) : (
                    <>
                      Ref · {evidenceRef} ·{' '}
                      <span className="italic">simulated for the atelier preview</span>
                    </>
                  )}
                </p>
              </div>
            )}
          </div>

          {/* Milestone rail */}
          <ol
            data-testid="living-vessel-rail"
            className="mt-10 grid grid-cols-4 gap-2"
          >
            {STAGES.map((s, i) => {
              const reached = i <= activeIdx;
              const current = i === activeIdx;
              return (
                <li key={s.id}>
                  <button
                    type="button"
                    data-testid={`living-vessel-step-${s.id}`}
                    onClick={() => {
                      setAutoplay(false);
                      setActiveIdx(i);
                    }}
                    className="w-full text-left group focus:outline-none"
                  >
                    <span
                      aria-hidden
                      className={`block h-px mb-3 transition-all duration-700 ${
                        reached ? 'bg-[#7C9CB1]' : 'bg-[#C1D7E2]'
                      } ${current ? 'h-[2px]' : ''}`}
                    />
                    <span
                      className={`block font-mono text-[10px] tracking-[0.28em] uppercase transition-colors ${
                        current
                          ? 'text-[#7C9CB1]'
                          : reached
                          ? 'text-[#90B0BF]'
                          : 'text-[#90B0BF]/60'
                      } group-hover:text-[#7C9CB1]`}
                    >
                      {s.no}. {s.label.replace(/^The\s+/i, '')}
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>

          <p className="mt-8 font-mono text-[10px] tracking-[0.28em] uppercase text-[#90B0BF]">
            {autoplay
              ? 'Reading itself · tap a chapter to take the helm'
              : 'You hold the helm'}
          </p>
        </div>

        {/* 3D vessel column */}
        <div className="md:col-span-7 order-1 md:order-2">
          <div
            data-testid="living-vessel-canvas-wrap"
            className="relative aspect-[4/5] md:aspect-[5/6] w-full max-w-[640px] mx-auto"
          >
            <BottleScene targetLevel={active.level} />

            {/* Live-fill readout, glass-card style */}
            <div className="absolute bottom-5 left-5 right-5 backdrop-blur-md bg-[#F4F8F9]/70 border border-[#C1D7E2] p-4 flex items-baseline justify-between gap-6">
              <p className="font-mono text-[9px] tracking-[0.3em] uppercase text-[#90B0BF]">
                Living Vessel · Stage {active.no}
              </p>
              <p
                data-testid="living-vessel-fill"
                className="font-serif text-2xl text-[#7C9CB1]"
              >
                {Math.round(active.level * 100)}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Local fade-in keyframe (scoped, no global side effects) */}
      <style>{`
        .story-fade-in {
          animation: storyFadeIn 700ms ease-out both;
        }
        @keyframes storyFadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .evidence-fade-in {
          animation: evidenceFadeIn 380ms ease-out both;
          transform-origin: top left;
        }
        @keyframes evidenceFadeIn {
          from { opacity: 0; transform: translateY(-4px) scaleY(0.98); }
          to   { opacity: 1; transform: translateY(0) scaleY(1); }
        }
      `}</style>
    </section>
  );
};

export default LivingVessel;
